import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { isNil, isUndefined } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { WalkingFarmMainTabEnum, WalkingFarmViewTabEnum } from '../../../../enums/walking-farm';
import * as fromWalkingFarmActions from '../../../../store/actions/walking-farm.actions';
import * as fromWalkingFarmSelectors from '../../../../store/selectors/walking-farm.selectors';
import { WalkingFarm } from '../../../../typings/walking-farm';
import { MapsService } from '../../../services/maps.service';
import { MapComponent } from './components/map/map.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-view',
  standalone: false,
  templateUrl: './view.component.html'
})
export class ViewPageComponent {
  private mapsService = inject(MapsService);
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);

  @ViewChild(MapComponent) mapComponent?: MapComponent;
  walkingFarmId: string | null;
  walkingFarm$: Observable<WalkingFarm>;
  filtersAppliedNumber$: Observable<number>;
  isNonDisclosureState$: Observable<boolean>;

  selectedTab = WalkingFarmViewTabEnum.Map;

  propertiesFilterText = '';

  defaultHref: string;

  readonly walkingFarmViewTabs = WalkingFarmViewTabEnum;

  constructor() {
    const defaultBackHref: string | undefined = this.activatedRoute.snapshot.data['defaultBackHref'];
    this.defaultHref = [defaultBackHref ?? '', 'walking-farm'].join('/');

    this.walkingFarmId = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.loadFarm({
          payload: { id: this.walkingFarmId }
        })
      );
    }
    this.walkingFarm$ = of(this.walkingFarmId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarm(id)))),
      filter((walkingFarm) => !isUndefined(walkingFarm))
    );
    this.filtersAppliedNumber$ = of(this.walkingFarmId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) =>
        this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmViewFiltersAppliedNumber(id)))
      ),
      filter((walkingFarm) => !isUndefined(walkingFarm))
    );
    this.isNonDisclosureState$ = of(this.walkingFarmId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmIsNonDisclosureState(id))))
    );
  }

  segmentChange(event: CustomEvent) {
    this.selectedTab = event.detail.value;
    this.propertiesFilterText = '';
  }

  async ionViewWillEnter() {
    await this.mapsService.enableWatchPosition();
    this.mapComponent?.centerOnBounds();
  }

  async ionViewWillLeave() {
    await this.mapsService.disableWatchPosition();
  }

  filterProperties(event: CustomEvent) {
    this.propertiesFilterText = event.detail.value;
  }

  clearFilterProperties() {
    this.propertiesFilterText = '';
  }

  protected readonly walkingFarmMainTabs = WalkingFarmMainTabEnum;
}
